import React from 'react';
import { TradingActivity } from '../../features/portfolio/types/tradingActivity';
import { normalizeToUtc20, parseLocalDate } from '../utils/dateHelpers';
import Modal from '../components/ui/Modal';

export interface TradingActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  activities: TradingActivity[];
  isWeeklyView?: boolean; // Add flag to indicate if this is a weekly view
  friendView?: boolean; // When true, show only 2 columns (Date and "Bought AAPL"), hide description
}

const TradingActivityModal: React.FC<TradingActivityModalProps> = ({
  isOpen,
  onClose,
  date: incomingDate,
  activities,
  isWeeklyView = false,
  friendView = false
}) => {
  // Normalize incoming date to 20:00 UTC to ensure consistent timezone display
  const normalizedHeaderDate = React.useMemo(() => {
    const result = normalizeToUtc20(incomingDate);
    return result;
  }, [incomingDate]);

  if (!isOpen) return null;

  // Format date for display
  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      
      if (isWeeklyView) {
        // For weekly views, show "Week of Monday, [Date]"
        return `Week of ${date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}`;
      } else {
        // For daily views, show the regular format
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    } catch (error) {
      return dateStr;
    }
  };

  // Sort activities by timestamp (earliest first)
  const sortedActivities = [...activities].sort((a, b) => {
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  });

  // Color classes for equity rows (keep existing green/red)
  const getEquityClasses = (action: string): string => {
    switch (action) {
      case 'BUY':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'SELL':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Inline style for non-equity instruments to match exact hex palette
  const getInstrumentStyle = (activity: TradingActivity): React.CSSProperties | undefined => {
    if (activity.instrument === 'OPTION') {
      if (activity.optionType === 'CALL') {
        return { color: '#00777D', backgroundColor: '#E1FEFF', borderColor: '#E1FEFF' };
      }
      if (activity.optionType === 'PUT') {
        return { color: '#A53387', backgroundColor: '#FFECFA', borderColor: '#FFECFA' };
      }
    }
    if (activity.instrument === 'DIVIDEND') {
      return { color: '#DE7A1E', backgroundColor: '#FFEDDD', borderColor: '#FFEDDD' };
    }
    if (activity.instrument === 'INTEREST') {
      return { color: '#89908B', backgroundColor: '#F3F3F3', borderColor: '#F3F3F3' };
    }
    if (activity.instrument === 'CASH') {
      return { color: '#89908B', backgroundColor: '#F3F3F3', borderColor: '#F3F3F3' };
    }
    return undefined;
  };

  // Get action icon, with dividend and options specific mapping
  const getActionIcon = (activity: TradingActivity): string => {
    if (activity.instrument === 'DIVIDEND') {
      return activity.event_type === 'DIVIDEND_IN_LIEU' ? '−' : '+';
    }
    
    if (activity.instrument === 'OPTION') {
      const et = activity.event_type || '';
      const intent = activity.intent || '';
      
      // CALL/PUT logic based on event_type and intent
      if ((et === 'CALL' || et === 'PUT') && intent === 'LONG') {
        return '+';
      }
      if ((et === 'CALL_SELL' || et === 'PUT_SELL') && intent === 'SHORT') {
        return '+';
      }
      if ((et === 'CALL' || et === 'PUT') && intent === 'SHORT') {
        return '−';
      }
      if ((et === 'CALL_SELL' || et === 'PUT_SELL') && intent === 'LONG') {
        return '−';
      }
    }
    
    // Default equity logic
    return activity.action === 'SELL' ? '−' : '+';
  };

  // Format date and time separately for the three-column layout
  const formatDateAndTime = (timestamp: string): { date: string; time: string } => {
    try {
      const timeValue = parseLocalDate(timestamp);
      const date = new Date(timeValue);
      
      if (isNaN(date.getTime())) {
        return { date: timestamp, time: '' };
      }
      
      // Format date as "M/D/YY"
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear().toString().slice(-2);
      const dateStr = `${month}/${day}/${year}`;
      
      // Format time as "H:MMAM/PM"
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const timeStr = `${displayHours}:${minutes}${ampm}`;
      
      return { date: dateStr, time: timeStr };
    } catch (error) {
      return { date: timestamp, time: '' };
    }
  };

  // Generate the middle column content based on instrument and event_type
  const getMiddleColumnContent = (activity: TradingActivity): string => {
    const { instrument, event_type, ticker } = activity;
    
    if (instrument === 'EQUITY') {
      switch (event_type) {
        case 'BUY':
          return `Bought ${ticker}`;
        case 'SELL':
          return `Sold ${ticker}`;
        case 'SHORT_SELL':
          return `Short ${ticker}`;
        case 'BUY_TO_COVER':
          return `Bought to Cover ${ticker}`;
        default:
          return ticker;
      }
    }
    
    if (instrument === 'DIVIDEND') {
      switch (event_type) {
        case 'DIVIDEND':
          return `${ticker} Dividend`;
        case 'DIVIDEND_IN_LIEU':
          return `${ticker} Dividend in Lieu`;
        default:
          return ticker;
      }
    }
    
    if (instrument === 'OPTION') {
      switch (event_type) {
        case 'CALL':
          return `${ticker} Call`;
        case 'CALL_SELL':
          return `${ticker} Call Sell`;
        case 'PUT':
          return `${ticker} Put`;
        case 'PUT_SELL':
          return `${ticker} Put Sell`;
        default:
          return ticker;
      }
    }
    
    if (instrument === 'INTEREST') {
      return 'Interest';
    }
    
    if (instrument === 'CASH') {
      // Show specific cash event type (DEPOSIT, WITHDRAWAL, ADJUSTMENT)
      switch (event_type) {
        case 'DEPOSIT':
          return 'Deposit';
        case 'WITHDRAWAL':
          return 'Withdrawal';
        case 'ADJUSTMENT':
          return 'Adjustment';
        default:
          return 'Cash';
      }
    }
    
    return ticker;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Activity - ${formatDate(normalizedHeaderDate)}`}
      size="lg"
      closeOnBackdropClick
    >
      <div className="space-y-4">
        {/* Activities List with Scrolling */}
        <div className="max-h-96 overflow-y-auto space-y-3">
          {sortedActivities.map((activity: TradingActivity, index: number) => {
            const { date, time } = formatDateAndTime(activity.timestamp);
            const middleContent = getMiddleColumnContent(activity);
            
            return (
              <div
                key={index}
                className={`relative pl-12 pr-4 py-4 rounded-lg transition-colors hover:bg-opacity-80 ${activity.instrument === 'EQUITY' ? getEquityClasses(activity.action) : ''}`}
                style={getInstrumentStyle(activity)}
              >
                <div 
                  className="grid items-start" 
                  style={{ 
                    gridTemplateColumns: friendView ? '50% 50%' : '25% 35% 40%' 
                  }}
                >
                  {/* Column 1: Date and Time */}
                  <div className="flex flex-col items-center pr-4">
                    <span className="text-base font-medium text-center">{date}</span>
                    <span className="text-base text-center">{time}</span>
                  </div>
                  
                  {/* Column 2: Type + Ticker + Split Info Icon */}
                  <div className="flex flex-col items-center justify-center pr-4">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base font-medium text-center">{middleContent}</span>
                      {activity.split_affected && (
                        <div className="relative group">
                          <img
                            src="/icon-info-lrg.png"
                            alt="Split adjusted"
                            className="w-4 h-4 cursor-help inline-block"
                          />
                          {/* Tooltip - positioned below to avoid modal header clipping */}
                          <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden group-hover:block z-[10000] pointer-events-none">
                            <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg whitespace-nowrap">
                              <p className="text-left">
                                Note: This transaction was adjusted due to a split.
                              </p>
                              {/* Tooltip arrow pointing up */}
                              <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Column 3: Description - Hidden for friend views */}
                  {!friendView && (
                    <div className="text-base text-center">
                      {activity.description?.trim() || ''}
                    </div>
                  )}
                </div>
                
                {/* Action Icon - positioned absolutely on the far left */}
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <span className="font-bold text-xl">
                    {getActionIcon(activity)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Show message if no activities */}
        {sortedActivities.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No trading activities found for this date.
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TradingActivityModal; 